/**
 * Guided Fix Panel Component (Elite Version)
 * ===========================================
 * Inline modal editing for manual fixes - removes navigation friction.
 * Users can fix data issues without leaving the readiness page.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { 
  Wrench, CheckCircle2, AlertCircle, RefreshCw, X,
  Building, Users, Briefcase, FileText, Zap, Edit3,
  ChevronDown, ChevronUp, Loader2
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Category icons and colors
const CATEGORY_CONFIG = {
  contractor: { icon: Users, color: "bg-blue-100 text-blue-600", label: "Contractor" },
  park: { icon: Building, color: "bg-green-100 text-green-600", label: "Park" },
  client: { icon: Briefcase, color: "bg-purple-100 text-purple-600", label: "Client" },
  job: { icon: FileText, color: "bg-amber-100 text-amber-600", label: "Job" }
};

// Form field schemas for each fix type
const FIX_SCHEMAS = {
  contractor_missing_email: {
    title: "Add Contractor Email",
    fields: [
      { name: "email", label: "Email Address", type: "email", required: true, placeholder: "contractor@example.com" }
    ]
  },
  contractor_missing_trades: {
    title: "Add Contractor Trades",
    fields: [
      { name: "trades", label: "Trades (comma separated)", type: "text", required: true, placeholder: "Electrical, Plumbing, Gas" }
    ]
  },
  contractor_missing_coverage: {
    title: "Add Coverage Area",
    fields: [
      { name: "coverage", label: "Regions (comma separated)", type: "text", required: true, placeholder: "Essex, Kent, London" }
    ]
  },
  contractor_mobile_fix: {
    title: "Fix Mobile Number",
    fields: [
      { name: "mobile", label: "Mobile Number", type: "tel", required: true, placeholder: "07700 900000" }
    ]
  },
  park_missing_contact_phone: {
    title: "Add Park Contact",
    fields: [
      { name: "contact_name", label: "Contact Name", type: "text", required: false, placeholder: "John Smith" },
      { name: "contact_phone", label: "Phone", type: "tel", required: true, placeholder: "01onal27 000000" },
      { name: "contact_email", label: "Email", type: "email", required: false, placeholder: "contact@park.com" }
    ]
  },
  park_missing_contact_email: {
    title: "Add Park Contact Email",
    fields: [
      { name: "contact_email", label: "Contact Email", type: "email", required: true, placeholder: "contact@park.com" }
    ]
  },
  park_missing_region: {
    title: "Set Park Region",
    fields: [
      { name: "region", label: "Region", type: "select", required: true, options: [
        "Essex", "Kent", "London", "Bedfordshire", "Hertfordshire", "Suffolk", "Norfolk", "Cambridgeshire", "Other"
      ]}
    ]
  },
  client_missing_billing_email: {
    title: "Add Billing Email",
    fields: [
      { name: "billing_email", label: "Billing Email", type: "email", required: true, placeholder: "billing@client.com" }
    ]
  },
  job_missing_contact: {
    title: "Add Job Contact",
    fields: [
      { name: "contact_name", label: "Contact Name", type: "text", required: false, placeholder: "Site Manager" },
      { name: "contact_phone", label: "Phone", type: "tel", required: true, placeholder: "07700 900000" }
    ]
  }
};

// Modal Component
const Modal = ({ open, onClose, children }) => {
  if (!open) return null;
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 relative animate-in zoom-in-95 duration-200">
        <button
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </button>
        {children}
      </div>
    </div>
  );
};

// Dynamic Form Field
const FormField = ({ field, value, onChange }) => {
  if (field.type === "select") {
    return (
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </label>
        <select
          value={value || ""}
          onChange={(e) => onChange(field.name, e.target.value)}
          className="w-full px-3 py-2 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
          required={field.required}
        >
          <option value="">Select {field.label.toLowerCase()}</option>
          {field.options?.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }
  
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {field.label} {field.required && <span className="text-red-500">*</span>}
      </label>
      <Input
        type={field.type || "text"}
        value={value || ""}
        onChange={(e) => onChange(field.name, e.target.value)}
        placeholder={field.placeholder}
        required={field.required}
        className="rounded-xl"
      />
    </div>
  );
};

export default function GuidedFixPanel({ onFixApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [applying, setApplying] = useState({});
  const [applyingAll, setApplyingAll] = useState(false);
  
  // Modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // View state
  const [viewMode, setViewMode] = useState("auto"); // "auto" | "manual" | "all"

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/readiness/autofix-suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load suggestions:", err);
      toast.error("Failed to load fix suggestions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuggestions();
  }, []);

  const applyAutoFix = async (item) => {
    const key = `${item.entity_id}-${item.action}`;
    setApplying(prev => ({ ...prev, [key]: true }));
    
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/api/readiness/autofix/apply`, {
        action: item.action,
        entity_id: item.entity_id,
        suggested_value: item.suggested_value
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.ok) {
        toast.success(res.data.message || "Fixed!");
        loadSuggestions();
        if (onFixApplied) onFixApplied();
      } else {
        toast.error(res.data.error || "Fix failed");
      }
    } catch (err) {
      toast.error("Failed to apply fix");
    } finally {
      setApplying(prev => ({ ...prev, [key]: false }));
    }
  };

  const applyAllAutoFixes = async () => {
    setApplyingAll(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/api/readiness/autofix/apply-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.ok) {
        toast.success(`Fixed ${res.data.success} items`);
        loadSuggestions();
        if (onFixApplied) onFixApplied();
      }
    } catch (err) {
      toast.error("Failed to apply fixes");
    } finally {
      setApplyingAll(false);
    }
  };

  const openManualFix = (item) => {
    setSelectedItem(item);
    // Pre-fill suggested value if available
    const initialData = {};
    if (item.suggested_value) {
      const schema = FIX_SCHEMAS[item.type];
      if (schema?.fields?.[0]) {
        initialData[schema.fields[0].name] = item.suggested_value;
      }
    }
    setFormData(initialData);
  };

  const handleFormChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const submitManualFix = async () => {
    if (!selectedItem) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/api/readiness/manual-fix`, {
        entity_id: selectedItem.entity_id,
        type: selectedItem.type,
        payload: formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.ok) {
        toast.success(res.data.message || "Fixed successfully!");
        setSelectedItem(null);
        setFormData({});
        loadSuggestions();
        if (onFixApplied) onFixApplied();
      } else {
        toast.error(res.data.error || "Fix failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save fix");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
        <div className="flex items-center gap-2 text-slate-500">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Loading fix suggestions...</span>
        </div>
      </div>
    );
  }

  if (!data || data.count === 0) {
    return (
      <div className="rounded-2xl border border-green-200 bg-green-50 shadow-sm p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-green-800">All Data Clean!</h3>
            <p className="text-sm text-green-600">No fixes needed at this time.</p>
          </div>
        </div>
      </div>
    );
  }

  const autoFixable = data.suggestions?.filter(s => s.auto_fixable) || [];
  const manualOnly = data.suggestions?.filter(s => !s.auto_fixable) || [];
  
  const displayItems = viewMode === "auto" ? autoFixable 
    : viewMode === "manual" ? manualOnly 
    : data.suggestions || [];

  const schema = selectedItem ? FIX_SCHEMAS[selectedItem.type] : null;
  const CategoryIcon = selectedItem ? CATEGORY_CONFIG[selectedItem.category]?.icon : AlertCircle;

  return (
    <>
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Header */}
        <div 
          className="p-5 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Wrench className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Guided Fix Panel</h3>
                <p className="text-sm text-slate-500">
                  {autoFixable.length} auto • {manualOnly.length} manual
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {autoFixable.length > 0 && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    applyAllAutoFixes();
                  }}
                  disabled={applyingAll}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  {applyingAll ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <Zap className="h-4 w-4 mr-1" />
                  )}
                  Fix All Auto ({autoFixable.length})
                </Button>
              )}
              {expanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
            </div>
          </div>
        </div>

        {/* Content */}
        {expanded && (
          <div className="p-5">
            {/* View Toggle */}
            <div className="flex gap-2 mb-4">
              {[
                { key: "auto", label: `Auto (${autoFixable.length})`, color: "bg-teal-600" },
                { key: "manual", label: `Manual (${manualOnly.length})`, color: "bg-slate-700" },
                { key: "all", label: "All", color: "bg-slate-500" }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setViewMode(tab.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    viewMode === tab.key 
                      ? `${tab.color} text-white` 
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Category Summary */}
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(data.by_category || {}).map(([cat, count]) => {
                if (count === 0) return null;
                const config = CATEGORY_CONFIG[cat] || { icon: AlertCircle, color: "bg-slate-100 text-slate-600" };
                const Icon = config.icon;
                return (
                  <div key={cat} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
                    <Icon className="h-3.5 w-3.5" />
                    {count}
                  </div>
                );
              })}
            </div>

            {/* Items List */}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {displayItems.slice(0, 50).map((item, i) => {
                const config = CATEGORY_CONFIG[item.category] || { icon: AlertCircle, color: "bg-slate-100 text-slate-600" };
                const Icon = config.icon;
                const key = `${item.entity_id}-${item.action}`;
                const isApplying = applying[key];
                
                return (
                  <div 
                    key={i} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${config.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm text-slate-800 truncate">
                          {item.entity_name}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {item.description}
                        </div>
                        {item.suggested_value && (
                          <div className="text-xs text-teal-600 mt-0.5 truncate">
                            → {item.suggested_value}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {item.auto_fixable ? (
                      <Button
                        onClick={() => applyAutoFix(item)}
                        disabled={isApplying}
                        size="sm"
                        className="bg-teal-600 hover:bg-teal-700 ml-2 flex-shrink-0"
                      >
                        {isApplying ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <Zap className="h-3 w-3 mr-1" />
                            Auto
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => openManualFix(item)}
                        size="sm"
                        className="bg-slate-700 hover:bg-slate-800 ml-2 flex-shrink-0"
                      >
                        <Edit3 className="h-3 w-3 mr-1" />
                        Fix
                      </Button>
                    )}
                  </div>
                );
              })}
              
              {displayItems.length > 50 && (
                <div className="text-center text-xs text-slate-400 py-2">
                  +{displayItems.length - 50} more items
                </div>
              )}
            </div>

            {/* Refresh */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <Button
                onClick={loadSuggestions}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Manual Fix Modal */}
      <Modal open={!!selectedItem} onClose={() => setSelectedItem(null)}>
        <div className="p-6">
          {/* Modal Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              CATEGORY_CONFIG[selectedItem?.category]?.color || "bg-slate-100"
            }`}>
              <CategoryIcon className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                {schema?.title || "Manual Fix"}
              </h3>
              <p className="text-sm text-slate-500">{selectedItem?.entity_name}</p>
            </div>
          </div>

          {/* Current Issue */}
          {selectedItem?.description && (
            <div className="mb-4 p-3 bg-amber-50 rounded-xl border border-amber-100">
              <div className="text-sm text-amber-800">
                <span className="font-medium">Issue:</span> {selectedItem.description}
              </div>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-4 mb-6">
            {schema?.fields?.map(field => (
              <FormField
                key={field.name}
                field={field}
                value={formData[field.name]}
                onChange={handleFormChange}
              />
            )) || (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Value</label>
                <Input
                  value={formData.value || ""}
                  onChange={(e) => handleFormChange("value", e.target.value)}
                  className="rounded-xl"
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={() => setSelectedItem(null)}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={submitManualFix}
              disabled={submitting}
              className="flex-1 bg-teal-600 hover:bg-teal-700"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Save Fix
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
